import { useState, useEffect, useRef } from 'react';
import { refreshClassroomStatus } from '../../../store/slices/selectedActivitySlice';
import { ActivityData } from '../../../interfaces/activity';
import useInterval from '../../../../common/hooks/useInterval';

// 150 polls × 2s interval = 5 min. P99 creation time is 13s, so anything
// still pending beyond this is almost certainly a data issue.
export const MAX_CLASSROOM_STATUS_POLLS = 150;

const useClassroomStatusPolling = (
    pollForClassroomUpdate: boolean,
    activity: ActivityData,
    dispatch,
    pollInterval: number,
    maxPolls: number = MAX_CLASSROOM_STATUS_POLLS,
) => {
    const activityId = activity?.pk;
    const pollCountRef = useRef(0);
    const [pollLimitReached, setPollLimitReached] = useState(false);

    // Reset counter when polling restarts for a new activity or re-enables
    useEffect(() => {
        if (pollForClassroomUpdate) {
            pollCountRef.current = 0;
            setPollLimitReached(false);
        }
    }, [pollForClassroomUpdate, activityId]);

    useInterval(
        async () => {
            if (pollForClassroomUpdate) {
                if (pollCountRef.current >= maxPolls) {
                    setPollLimitReached(true);
                    return;
                }
                pollCountRef.current += 1;
                // Use refreshClassroomStatus rather than getSelectedActivity to avoid
                // overwriting user-editable fields with a stale read mid-edit.
                await dispatch(refreshClassroomStatus(activityId));
            }
        },
        pollForClassroomUpdate && activityId && !pollLimitReached
            ? pollInterval
            : null,
    );

    return { pollLimitReached };
};

export default useClassroomStatusPolling;
